"use client";

import {
  animate,
  type MotionValue,
  useMotionValue,
  useReducedMotion,
} from "motion/react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  type Box3,
  type BoxPaths,
  createProjector,
  DEFAULT_PROJECTION_PAD,
  PROJECTION_POSES,
  type ProjectionFrame,
  type ProjectionKind,
  type ProjectionPoses,
  type ViewTransform,
} from "./iso-projection";

export type ProjectionMode = "flat" | "isometric";

export const PROJECTION_TRANSITION = {
  duration: 1.1,
  ease: [0.65, 0, 0.35, 1] as [number, number, number, number],
};

export interface ChartProjection {
  mode: ProjectionMode;
  kind: ProjectionKind;
  poses: ProjectionPoses;
  frame: ProjectionFrame;
  e: MotionValue<number>;
  settled: boolean;
  isometric: boolean;
  depth: number;
  at(e?: number): ViewTransform;
  project(x: number, y: number, z: number, e?: number): [number, number];
  projectBox(b: Box3, e?: number): BoxPaths;
}

const ChartProjectionContext = createContext<ChartProjection | null>(null);

export interface ChartProjectionProviderProps {
  mode: ProjectionMode;
  kind: ProjectionKind;
  width: number;
  height: number;
  depth: number;
  zExtent?: number;
  pad?: number;
  onSettledChange?: (settled: boolean) => void;
  children: ReactNode;
}

const targetOf = (mode: ProjectionMode): number => (mode === "isometric" ? 1 : 0);

export function ChartProjectionProvider({
  mode,
  kind,
  width,
  height,
  depth,
  zExtent,
  pad = DEFAULT_PROJECTION_PAD,
  onSettledChange,
  children,
}: ChartProjectionProviderProps) {
  const reducedMotion = useReducedMotion();
  const e = useMotionValue(targetOf(mode));
  const [settled, setSettled] = useState(true);
  const onSettledRef = useRef(onSettledChange);
  onSettledRef.current = onSettledChange;

  useEffect(() => {
    const target = targetOf(mode);
    if (e.get() === target) {
      setSettled(true);
      return;
    }
    if (reducedMotion) {
      e.jump(target);
      setSettled(true);
      return;
    }
    setSettled(false);
    const controls = animate(e, target, {
      ...PROJECTION_TRANSITION,
      onComplete: () => setSettled(true),
    });
    return () => controls.stop();
  }, [mode, reducedMotion, e]);

  useEffect(() => {
    onSettledRef.current?.(settled);
  }, [settled]);

  const poses = PROJECTION_POSES[kind];
  const zExtentResolved = zExtent ?? height;

  const frame = useMemo<ProjectionFrame>(
    () => ({
      width: Math.max(0, width),
      height: Math.max(0, height),
      depth: Math.max(0, depth),
      zExtent: Math.max(0, zExtentResolved),
      poses,
      pad,
    }),
    [width, height, depth, zExtentResolved, poses, pad]
  );

  const value = useMemo<ChartProjection>(() => {
    const projector = createProjector(frame);
    return {
      mode,
      kind,
      poses,
      frame,
      e,
      settled,
      isometric: mode === "isometric",
      depth: frame.depth,
      at: (v) => projector.at(v ?? e.get()),
      project: (x, y, z, v) => projector.project(x, y, z, v ?? e.get()),
      projectBox: (b, v) => projector.projectBox(b, v ?? e.get()),
    };
  }, [frame, mode, kind, poses, e, settled]);

  return (
    <ChartProjectionContext.Provider value={value}>
      {children}
    </ChartProjectionContext.Provider>
  );
}

export function useChartProjection(): ChartProjection | null {
  return useContext(ChartProjectionContext);
}
